import React, { useState, useEffect, useMemo } from 'react';
import { phoneNumbers } from '../api';
import {
    Phone, Trash2, Loader2, RefreshCw, Search, Info,
    List, CheckCircle, XCircle, AlertTriangle,
} from 'lucide-react';

// ─── Provider badge helper ──────────────────────────────────────────────────

function ProviderBadge({ number }) {
    if ((number.provider || '').toLowerCase() === 'vonage') {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/30">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                Vonage
            </span>
        );
    }
    if (number.twilio_account_sid) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                Twilio
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
            Telnyx
        </span>
    );
}

function getProviderKey(number) {
    if ((number.provider || '').toLowerCase() === 'vonage') return 'vonage';
    if (number.twilio_account_sid) return 'twilio';
    return 'telnyx';
}

// ─── Log panel (compact, matches rest of app) ───────────────────────────────

function LogPanel({ logs, onClear }) {
    const getIcon = (type) => {
        if (type === 'success') return <CheckCircle className="w-4 h-4" />;
        if (type === 'error') return <XCircle className="w-4 h-4" />;
        if (type === 'warning') return <AlertTriangle className="w-4 h-4" />;
        return <Info className="w-4 h-4" />;
    };
    const getColor = (type) => {
        if (type === 'success') return 'text-success';
        if (type === 'error') return 'text-destructive';
        if (type === 'warning') return 'text-yellow-500';
        return 'text-primary';
    };

    return (
        <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 flex flex-col min-h-0 shadow-elegant lg:sticky lg:top-4 lg:self-start">
            <div className="flex justify-between items-center mb-4 flex-shrink-0 gap-2">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                        <List className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground truncate">Live Logs</h2>
                </div>
                <button
                    onClick={onClear}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-border/60 bg-background/50 hover:bg-background hover:border-primary/20 transition-all duration-300 text-foreground flex-shrink-0"
                >
                    <Trash2 className="w-4 h-4" />
                    Clear
                </button>
            </div>
            <div className="flex-1 bg-background/30 border border-border/40 rounded-lg p-4 overflow-y-auto font-mono text-sm min-h-[280px] max-h-[min(70vh,calc(100vh-10rem))]">
                {logs.length === 0 ? (
                    <div className="text-muted-foreground flex items-center justify-center h-full">
                        <div className="text-center">
                            <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No logs yet.</p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {logs.map((log) => (
                            <div
                                key={log.id}
                                className={`flex items-start gap-2 py-1.5 px-2 rounded ${getColor(log.type)}`}
                            >
                                <span className="text-muted-foreground text-xs mt-0.5">[{log.timestamp}]</span>
                                <span className="flex-shrink-0 mt-0.5">{getIcon(log.type)}</span>
                                <span className="flex-1">{log.message}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Main component ─────────────────────────────────────────────────────────

const AllNumbersView = () => {
    const [numbers, setNumbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removingId, setRemovingId] = useState(null);
    const [filter, setFilter] = useState('all'); // 'all' | 'telnyx' | 'vonage' | 'twilio'
    const [search, setSearch] = useState('');
    const [logs, setLogs] = useState([]);

    const addLog = (message, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs((prev) => [...prev, { id: Date.now() + Math.random(), message, type, timestamp }].slice(-30));
    };

    const loadNumbers = async () => {
        setLoading(true);
        try {
            const res = await phoneNumbers.list();
            const data = res.data || [];
            setNumbers(data);
            addLog(`✅ Loaded ${data.length} phone number${data.length === 1 ? '' : 's'}`, 'success');
        } catch (err) {
            addLog(`❌ Failed to load numbers: ${err.response?.data?.detail || err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadNumbers(); }, []);

    // Stats by provider
    const stats = useMemo(() => {
        const s = { total: numbers.length, telnyx: 0, vonage: 0, twilio: 0 };
        numbers.forEach((n) => { s[getProviderKey(n)] += 1; });
        return s;
    }, [numbers]);

    // Filtered list
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return numbers.filter((n) => {
            if (filter !== 'all' && getProviderKey(n) !== filter) return false;
            if (!q) return true;
            return (
                (n.e164 || '').toLowerCase().includes(q) ||
                (n.agent_name || '').toLowerCase().includes(q)
            );
        });
    }, [numbers, filter, search]);

    const handleRemove = async (number) => {
        if (!window.confirm(
            `Remove phone number ${number.e164} from agent ${number.agent_name || '(none)'}?\n\n` +
            `This will delete the associated LiveKit SIP trunks and dispatch rules.`,
        )) return;
        setRemovingId(number.id);
        addLog(`🗑️ Removing ${number.e164}...`, 'info');
        try {
            const response = await phoneNumbers.delete(number.id);
            const cleanup = response.data?.cleanup || [];
            cleanup.forEach((msg) => addLog(`  ↳ ${msg}`, 'info'));
            addLog(`✅ Removed ${number.e164} successfully`, 'success');
            await loadNumbers();
        } catch (err) {
            addLog(
                `❌ Failed to remove ${number.e164}: ${err.response?.data?.detail || err.message}`,
                'error',
            );
        } finally {
            setRemovingId(null);
        }
    };

    const FilterPill = ({ value, label, count, dotClass }) => (
        <button
            onClick={() => setFilter(value)}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                filter === value
                    ? 'bg-gradient-button text-primary-foreground border-transparent shadow-md'
                    : 'text-muted-foreground hover:text-foreground bg-card/60 hover:bg-card border-border/50 hover:border-border'
            }`}
        >
            {dotClass && <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />}
            {label}
            <span
                className={`ml-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    filter === value
                        ? 'bg-white/20 text-primary-foreground'
                        : 'bg-border/40 text-foreground/70'
                }`}
            >
                {count}
            </span>
        </button>
    );

    return (
        <div className="flex-1 flex flex-col min-h-0">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-foreground mb-1">Phone Numbers</h1>
                <p className="text-muted-foreground">
                    All numbers assigned across providers — view, search, and remove from one place.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(280px,360px)] gap-6 items-start min-h-0">
                <div className="min-w-0 flex flex-col gap-4">
                    {/* Toolbar */}
                    <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-4 shadow-elegant flex flex-col gap-3">
                        <div className="flex items-center gap-3 flex-wrap">
                            <FilterPill value="all" label="All" count={stats.total} />
                            <FilterPill value="telnyx" label="Telnyx" count={stats.telnyx} dotClass="bg-purple-400" />
                            <FilterPill value="vonage" label="Vonage" count={stats.vonage} dotClass="bg-orange-400" />
                            {stats.twilio > 0 && (
                                <FilterPill value="twilio" label="Twilio" count={stats.twilio} dotClass="bg-blue-400" />
                            )}
                            <button
                                onClick={loadNumbers}
                                className="ml-auto flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-border/60 bg-background/50 hover:bg-background hover:border-primary/20 transition-all duration-300 text-foreground text-xs font-medium"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                            </button>
                        </div>

                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by number or agent..."
                                className="w-full bg-background/50 border border-border/60 text-foreground rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all placeholder:text-muted-foreground/50"
                            />
                        </div>
                    </div>

                    {/* Numbers table */}
                    <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 flex flex-col min-h-0 shadow-elegant">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                                <Phone className="w-5 h-5 text-primary-foreground" />
                            </div>
                            <h2 className="text-xl font-semibold text-foreground">
                                Assigned Numbers
                                <span className="ml-2 text-sm font-normal text-muted-foreground">
                                    ({filtered.length}{filter !== 'all' || search ? ` of ${numbers.length}` : ''})
                                </span>
                            </h2>
                        </div>

                        {loading && numbers.length === 0 ? (
                            <div className="flex items-center justify-center py-16 text-muted-foreground">
                                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading numbers...
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="text-center py-16 text-muted-foreground">
                                <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p className="font-medium">
                                    {numbers.length === 0
                                        ? 'No phone numbers yet'
                                        : 'No numbers match your filters'}
                                </p>
                                <p className="text-sm mt-1">
                                    {numbers.length === 0
                                        ? 'Buy a Telnyx or Vonage number to get started.'
                                        : 'Try clearing the search or provider filter.'}
                                </p>
                            </div>
                        ) : (
                            <div className="flex-1 overflow-auto min-h-0">
                                <table className="min-w-full">
                                    <thead className="sticky top-0 bg-background/80 backdrop-blur-sm border-b border-border/60">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Phone Number</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Agent</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Provider</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Created</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((number, idx) => (
                                            <tr
                                                key={number.id}
                                                className={`border-b border-border/20 ${idx % 2 === 0 ? 'bg-background/20' : ''} hover:bg-background/30 transition-colors`}
                                            >
                                                <td className="px-4 py-3 text-sm font-mono text-foreground">{number.e164}</td>
                                                <td className="px-4 py-3 text-sm text-foreground">{number.agent_name || <span className="text-muted-foreground italic">—</span>}</td>
                                                <td className="px-4 py-3 text-sm">
                                                    <ProviderBadge number={number} />
                                                </td>
                                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                                    {number.created_at ? new Date(number.created_at).toLocaleDateString() : 'N/A'}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <button
                                                        onClick={() => handleRemove(number)}
                                                        disabled={removingId === number.id}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20 hover:border-destructive/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {removingId === number.id
                                                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                            : <Trash2 className="w-3.5 h-3.5" />}
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <LogPanel logs={logs} onClear={() => setLogs([])} />
            </div>
        </div>
    );
};

export default AllNumbersView;
